import {
	type AssetFamily,
	type AssetType,
	type MasterAssetType,
	createOrganizationAssetType,
	deleteOrganizationAssetType,
	getAssetFamilies,
	getMasterAssetTypes,
	updateOrganizationAssetType,
} from "@/services/utility-service";
import {
	Button,
	Form,
	Input,
	InputNumber,
	Modal,
	Popconfirm,
	Select,
	Space,
	Switch,
	Table,
	Tag,
	message,
} from "antd";
import type { FC } from "react";
import { useCallback, useEffect, useState } from "react";

interface AssetTypeModalProps {
	visible: boolean;
	onClose: () => void;
	onSuccess: () => void;
	currentAssetTypes: AssetType[];
}

export const AssetTypeModal: FC<AssetTypeModalProps> = ({
	visible,
	onClose,
	onSuccess,
	currentAssetTypes,
}) => {
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);
	const [masterAssetTypes, setMasterAssetTypes] = useState<MasterAssetType[]>(
		[],
	);
	const [assetFamilies, setAssetFamilies] = useState<AssetFamily[]>([]);
	const [masterAssetTypesLoading, setMasterAssetTypesLoading] = useState(false);
	const [pageSize, setPageSize] = useState(5);

	const fetchMasterAssetTypes = useCallback(async () => {
		try {
			setMasterAssetTypesLoading(true);
			const [masterTypes, families] = await Promise.all([
				getMasterAssetTypes(),
				getAssetFamilies(),
			]);
			setMasterAssetTypes(masterTypes);
			setAssetFamilies(families);
		} catch (error: any) {
			message.error(error.message || "Failed to fetch master asset types");
		} finally {
			setMasterAssetTypesLoading(false);
		}
	}, []);

	useEffect(() => {
		if (visible) {
			fetchMasterAssetTypes();
		}
	}, [visible, fetchMasterAssetTypes]);

	const handleSubmit = async (values: any) => {
		try {
			setLoading(true);
			await createOrganizationAssetType({
				masterAssetTypeId: values.masterAssetTypeId,
				name: values.name,
				organizationAssetTypeCategoryId: values.organizationAssetTypeCategoryId,
				conditionalInputs: values.conditionalInputs,
				isDeprecated: values.isDeprecated || false,
				inputsNotRequired: values.inputsNotRequired || false,
				organizationEquipmentAreaId: values.organizationEquipmentAreaId,
				extra: values.extra,
				subOrganization: values.subOrganization,
			});

			message.success("Asset type created successfully");
			form.resetFields();
			onSuccess();
		} catch (error: any) {
			message.error(error.message || "Failed to create asset type");
		} finally {
			setLoading(false);
		}
	};

	const handleDeprecate = async (
		assetTypeId: number,
		currentDeprecatedStatus: boolean,
	) => {
		try {
			await updateOrganizationAssetType(assetTypeId, {
				isDeprecated: !currentDeprecatedStatus,
			});
			message.success(
				currentDeprecatedStatus
					? "Asset type restored successfully"
					: "Asset type deprecated successfully",
			);
			onSuccess();
		} catch (error: any) {
			message.error(error.message || "Failed to update asset type");
		}
	};

	const handleDelete = async (assetTypeId: number) => {
		try {
			await deleteOrganizationAssetType(assetTypeId);
			message.success("Asset type deleted successfully");
			onSuccess();
		} catch (error: any) {
			message.error(error.message || "Failed to delete asset type");
		}
	};

	const handleCancel = () => {
		form.resetFields();
		onClose();
	};

	const assetTypeColumns = [
		{
			title: "Name",
			dataIndex: "name",
			key: "name",
		},
		{
			title: "Master Asset Type",
			key: "masterAssetType",
			render: (_: any, record: AssetType) => {
				const masterType = masterAssetTypes.find(
					(mt) => mt.id === record.masterAssetTypeId,
				);
				return masterType ? masterType.name : `ID: ${record.masterAssetTypeId}`;
			},
		},
		{
			title: "Category ID",
			dataIndex: "organizationAssetTypeCategoryId",
			key: "organizationAssetTypeCategoryId",
			render: (value: number) => value || "-",
		},
		{
			title: "Equipment Area ID",
			dataIndex: "organizationEquipmentAreaId",
			key: "organizationEquipmentAreaId",
			render: (value: number) => value || "-",
		},
		{
			title: "Sub Organization",
			dataIndex: "subOrganization",
			key: "subOrganization",
			render: (value: string) => value || "-",
		},
		{
			title: "Status",
			key: "status",
			render: (_: any, record: AssetType) => (
				<Space>
					{record.isDeprecated && <Tag color="red">Deprecated</Tag>}
					{record.inputsNotRequired && (
						<Tag color="blue">No Inputs Required</Tag>
					)}
				</Space>
			),
		},
		{
			title: "Actions",
			key: "actions",
			render: (_: any, record: AssetType) => (
				<Space>
					<Popconfirm
						title={
							record.isDeprecated
								? "Are you sure you want to restore this asset type?"
								: "Are you sure you want to deprecate this asset type? It will no longer appear in submission forms."
						}
						onConfirm={() => handleDeprecate(record.id, record.isDeprecated)}
						okText="Yes"
						cancelText="No"
					>
						<Button
							type="link"
							style={{ color: record.isDeprecated ? "#52c41a" : "#faad14" }}
						>
							{record.isDeprecated ? "Restore" : "Deprecate"}
						</Button>
					</Popconfirm>
					<Popconfirm
						title="Are you sure you want to permanently delete this asset type? This action cannot be undone."
						description="Note: Asset types in use by existing assets cannot be deleted."
						onConfirm={() => handleDelete(record.id)}
						okText="Yes"
						cancelText="No"
					>
						<Button type="link" danger>
							Delete
						</Button>
					</Popconfirm>
				</Space>
			),
		},
	];

	return (
		<Modal
			title="Manage Organization Asset Types"
			open={visible}
			onCancel={handleCancel}
			footer={[
				<Button key="cancel" onClick={handleCancel}>
					Close
				</Button>,
			]}
			width={1200}
		>
			<div className="space-y-6">
				{/* Current Asset Types Table */}
				<div>
					<h3 className="text-lg font-medium mb-4">Current Asset Types</h3>
					<Table
						columns={assetTypeColumns}
						dataSource={currentAssetTypes}
						loading={masterAssetTypesLoading}
						rowKey="id"
						pagination={{
							pageSize: pageSize,
							showSizeChanger: true,
							pageSizeOptions: ["5", "10", "20", "50"],
							onShowSizeChange: (current, size) => {
								setPageSize(size);
							},
							showQuickJumper: true,
							showTotal: (total, range) =>
								`${range[0]}-${range[1]} of ${total} asset types`,
						}}
						scroll={{ x: 800 }}
					/>
				</div>

				{/* Add New Asset Type Form */}
				<div className="border-t pt-6">
					<h3 className="text-lg font-medium mb-4">Add New Asset Type</h3>
					<Form
						form={form}
						layout="vertical"
						onFinish={handleSubmit}
						initialValues={{
							isDeprecated: false,
							inputsNotRequired: false,
						}}
					>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<Form.Item
								name="masterAssetTypeId"
								label="Master Asset Type"
								rules={[
									{ required: true, message: "Master Asset Type is required" },
								]}
							>
								<Select
									placeholder="Select master asset type"
									loading={masterAssetTypesLoading}
									showSearch
									optionFilterProp="children"
									filterOption={(input, option) =>
										(option?.label ?? "")
											.toLowerCase()
											.includes(input.toLowerCase())
									}
									options={masterAssetTypes.map((mt) => ({
										value: mt.id,
										label: `${mt.name} (ID: ${mt.id})`,
									}))}
								/>
							</Form.Item>

							<Form.Item
								name="name"
								label="Asset Type Name"
								rules={[
									{ required: true, message: "Asset type name is required" },
									{ min: 1, message: "Name must be at least 1 character" },
								]}
							>
								<Input placeholder="Enter asset type name" />
							</Form.Item>

							<Form.Item
								name="organizationAssetTypeCategoryId"
								label="Category ID (Optional)"
							>
								<InputNumber
									placeholder="Enter category ID"
									style={{ width: "100%" }}
								/>
							</Form.Item>

							<Form.Item
								name="organizationEquipmentAreaId"
								label="Equipment Area ID (Optional)"
							>
								<InputNumber
									placeholder="Enter equipment area ID"
									style={{ width: "100%" }}
								/>
							</Form.Item>

							<Form.Item
								name="subOrganization"
								label="Sub Organization (Optional)"
							>
								<Input placeholder="Enter sub organization" />
							</Form.Item>

							<div className="flex space-x-4">
								<Form.Item
									name="isDeprecated"
									label="Is Deprecated"
									valuePropName="checked"
								>
									<Switch />
								</Form.Item>

								<Form.Item
									name="inputsNotRequired"
									label="Inputs Not Required"
									valuePropName="checked"
								>
									<Switch />
								</Form.Item>
							</div>
						</div>

						<div className="flex justify-end space-x-2 mt-4">
							<Button onClick={() => form.resetFields()}>Reset</Button>
							<Button type="primary" htmlType="submit" loading={loading}>
								Add Asset Type
							</Button>
						</div>
					</Form>
				</div>
			</div>
		</Modal>
	);
};
